import I18n from 'i18n-js';
import { reject } from 'lodash';
import React, { useContext } from 'react';

import { RCCheckbox } from '@reverbdotcom/commons/src/rc/forms';
import { RCAlertBox } from '@reverbdotcom/commons/src/rc';
import { RCTag } from '@reverbdotcom/commons/src/rc/RCTag';
import { US } from '@reverbdotcom/commons/src/constants';

import { SellFormSection } from '../SellFormSection';
import { Field, ActionType } from '../../sellFormReducer';
import { SellFormContext } from '../../SellFormContext';
import ShippingProfileCard from './ShippingProfileCard';
import DomesticShippingCard from './DomesticShippingCard';
import PrefersReverbShippingLabelCard from './PrefersReverbShippingLabelCard';
import InternationalShippingCard from './InternationalShippingCard';

interface IProps {
  completed: boolean;
  scrollRef: React.MutableRefObject<HTMLDivElement>;
}

export const FREE_RATE_ATTRS = {
  rateType: null,
  rate: { amount: '0' },
  incrementalRate: null,
  expeditedRate: null,
};

export const BLANK_RATE_ATTRS = {
  rateType: null,
  rate: null,
  incrementalRate: null,
  expeditedRate: null,
};

export const CARRIER_CALC_RATE_TYPE = 'carrier_calculated';
export const REGIONAL_RATE_TYPE = 'regional';

export function upsertShippingRate(existingRates, updatedShippingRate) {
  const targetIndex = existingRates.findIndex((sr) => sr.regionCode === updatedShippingRate.regionCode);
  const updatedShippingRates = existingRates.slice();

  if (targetIndex > -1) {
    updatedShippingRates[targetIndex] = updatedShippingRate;
  } else {
    updatedShippingRates.push(updatedShippingRate);
  }

  return updatedShippingRates;
}

export function deleteShippingRate(existingRates, regionCodeToDelete) {
  return reject(existingRates, (sr) => sr.regionCode === regionCodeToDelete);
}

export function buildBlankShippingRate(regionCode, rateType = null) {
  return {
    ...BLANK_RATE_ATTRS,
    regionCode,
    rateType,
  };
}

function ShippingForm({ completed, scrollRef }: IProps) {
  const {
    formState,
    dispatch,
    sellFormViewData,
    loading,
    showRequiredFieldError,
  } = useContext(SellFormContext);
  const { shippingRegionSettings, address, sameDayShippingConfigured, shippingProfiles = [] } = sellFormViewData.fetchedListing.shop;

  const domesticRegion = shippingRegionSettings.find(({ isDomestic }) => isDomestic);
  const selectedShippingProfile = shippingProfiles.find((sp) => sp.id === formState[Field.SHIPPING_PROFILE_ID]);
  const listingOffersShipping = formState[Field.SHIPPING_RATES].length > 0;
  const inCarrierCalculatedMode = !!formState[Field.CARRIER_CALCULATED_PACKAGE];
  const showRslCard = listingOffersShipping && !inCarrierCalculatedMode && address.countryCode === US;

  const errorText = (showRequiredFieldError && !completed) &&
    I18n.t('discovery.sellForm.shippingSection.requiredErrorText');

  return (
    <>
      <div ref={!completed ? scrollRef : null}/>
      {errorText && (
        <div className="mb-2">
          <RCAlertBox type="error" small>
            {errorText}
          </RCAlertBox>
        </div>
      )}

      {(shippingProfiles.length > 0) && <ShippingProfileCard/>}

      {!selectedShippingProfile && (
        <SellFormSection.Card>
          <SellFormSection.FormSection
            title={I18n.t('discovery.sellForm.shippingSection.deliveryOptionsHeader')}
            subtitle={I18n.t('discovery.sellForm.shippingSection.shippingOptionsCard.subheader')}
            tag="required"
          >
            <div className="mb-1">
              <RCCheckbox
                id="listingOffersShipping"
                name="listingOffersShipping"
                label={
                  <>
                    <span className="mr-1">{I18n.t('discovery.sellForm.shippingSection.deliveryOptions.shipping')}</span>
                    <span>
                      <RCTag variant="highlight">
                        {I18n.t('discovery.sellForm.shippingSection.deliveryOptions.shippingHighlight')}
                      </RCTag>
                    </span>
                  </>
                }
                checked={listingOffersShipping}
                onChange={() => {
                  if (listingOffersShipping) {
                    dispatch({ type: ActionType.SET_NO_SHIPPING });
                  } else {
                    dispatch({ type: ActionType.SET_DEFAULT_SHIPPING, shippingRates: [buildBlankShippingRate(domesticRegion.regionCode)] });
                  }
                }}
                disabled={loading}
              />
            </div>

            <RCCheckbox
              id="listingOffersLocalPickup"
              name="listingOffersLocalPickup"
              label={I18n.t('discovery.sellForm.shippingSection.deliveryOptions.localPickup')}
              checked={formState[Field.OFFERS_LOCAL_PICKUP]}
              onChange={() => dispatch({
                type: ActionType.UPDATE_FIELD,
                fieldKey: Field.OFFERS_LOCAL_PICKUP,
                value: !formState[Field.OFFERS_LOCAL_PICKUP],
              })}
              disabled={loading}
            />
          </SellFormSection.FormSection>
        </SellFormSection.Card>
      )}

      {listingOffersShipping && <DomesticShippingCard/>}

      {sameDayShippingConfigured && listingOffersShipping && (
        <>
          <SellFormSection.Card>
            <SellFormSection.FormSection
              title={I18n.t('discovery.sellForm.shippingSection.sameDayCard.header')}
              subtitle={I18n.t('discovery.sellForm.shippingSection.sameDayCard.subheader')}
            >
              <RCCheckbox
                id="sameDayShippingEligible"
                name="sameDayShippingEligible"
                label={I18n.t('discovery.sellForm.shippingSection.sameDayCard.label')}
                checked={!formState[Field.SAME_DAY_SHIPPING_INELIGIBLE]}
                onChange={() => dispatch({
                  type: ActionType.UPDATE_FIELD,
                  fieldKey: Field.SAME_DAY_SHIPPING_INELIGIBLE,
                  value: !formState[Field.SAME_DAY_SHIPPING_INELIGIBLE],
                })}
                disabled={loading}
              />
            </SellFormSection.FormSection>
          </SellFormSection.Card>
        </>
      )}

      {showRslCard && <PrefersReverbShippingLabelCard/>}

      {listingOffersShipping && <InternationalShippingCard />}
    </>
  );
}

export default ShippingForm;
